import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ExternalLink, AlertTriangle, ArrowRight, Loader2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TaxUpdates() {
  const { t, isAr } = useLang();
  const Arrow = ({ className = "" }) => <ArrowRight className={`${className} ${isAr ? "rotate-180" : ""}`} />;
  const [updates, setUpdates] = useState(null);

  useEffect(() => {
    axios.get(`${API}/updates`).then((r) => setUpdates(r.data)).catch(() => setUpdates([]));
  }, []);

  return (
    <div className="bg-ivory">
      <section className="relative overflow-hidden border-b border-line" data-testid="updates-hero">
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: `url(${IMAGES.brandTexture})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-ivory/50 to-ivory" />
        <div className="container-tnsq relative py-20 lg:py-24">
          <Reveal>
            <div className="text-sm text-muted mb-5">
              <Link to="/" className="hover:text-navy">{t({ en: "Home", ar: "الرئيسية" })}</Link>
              <span className="mx-2">/</span>
              <span className="text-ink font-medium">{t({ en: "Tax Update Hub", ar: "مركز التحديثات" })}</span>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <span className="kicker gold-line">{t({ en: "Regulatory Feed", ar: "التحديثات التشريعية" })}</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.1] max-w-4xl mt-4">
              {t({ en: "UAE Tax Update Hub", ar: "مركز تحديثات الضرائب في الإمارات" })}
            </h1>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-6 text-muted text-base md:text-lg max-w-2xl leading-relaxed">
              {t({ en: "Selected tax and compliance developments that materially affect UAE businesses, drawn from official FTA / MoF and government sources.", ar: "أهم التطورات الضريبية التي تؤثر فعلياً على الأعمال في الإمارات، من مصادر الهيئة ووزارة المالية والجهات الحكومية الرسمية." })}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="updates-list">
        <div className="container-tnsq max-w-4xl">
          <Reveal>
            <div className="flex gap-3 rounded-2xl border border-gold/40 bg-gold/[0.06] px-5 py-4 mb-12">
              <AlertTriangle className="h-5 w-5 text-gold shrink-0 mt-0.5" />
              <p className="text-sm text-ink/70 leading-relaxed">
                {t({ en: "This page is a practical summary, not the legislation itself. Always verify with the official FTA / MoF source before acting.", ar: "هذه الصفحة ملخص عملي وليست نص التشريع. يُرجى التحقق من مصدر الهيئة أو وزارة المالية الرسمي قبل اتخاذ أي إجراء." })}
              </p>
            </div>
          </Reveal>

          {updates === null && <div className="grid place-items-center py-10"><Loader2 className="h-7 w-7 animate-spin text-navy" /></div>}
          {updates && updates.length === 0 && <p className="text-muted">{t({ en: "No updates published yet.", ar: "لا توجد تحديثات منشورة بعد." })}</p>}

          <div className="space-y-6">
            {(updates || []).map((u, i) => {
              const title = isAr ? u.title_ar || u.title_en : u.title_en;
              const summary = isAr ? u.summary_ar || u.summary_en : u.summary_en;
              return (
                <Reveal key={u.id} delay={(i % 3) * 70}>
                  <article data-testid={`update-full-${u.id}`}
                    className={`grid sm:grid-cols-[120px_1fr] gap-6 rounded-2xl border p-6 sm:p-8 shadow-card ${i === 0 ? "border-brandgreen/40 bg-brandgreen/[0.04]" : "border-line bg-white"}`}>
                    <div className="flex sm:flex-col items-center sm:items-start gap-2">
                      <div className="rounded-xl bg-navy-deep text-white text-center px-4 py-3 leading-tight" dir="ltr">
                        <div className="font-display text-xl text-gold">{(u.date || "").slice(8, 10) || "—"}</div>
                        <div className="text-[0.65rem] tracking-wider text-white/60">{(u.date || "").slice(0, 7)}</div>
                      </div>
                      {i === 0 && <span className="text-[0.6rem] font-bold tracking-wider uppercase bg-brandgreen text-white px-2.5 py-1 rounded-full">{t({ en: "New", ar: "جديد" })}</span>}
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-brandgreen uppercase tracking-wider mb-1">{u.category}</div>
                      <h2 className="font-display text-xl sm:text-2xl text-ink leading-snug">{title}</h2>
                      <p className="mt-3 text-muted leading-relaxed">{summary}</p>
                      {u.show_source && u.source_url && (
                        <a href={u.source_url} target="_blank" rel="noopener noreferrer" data-testid={`update-source-${u.id}`}
                          className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-brandgreen hover:text-brandgreen-600 transition-colors">
                          <ExternalLink className="h-4 w-4" /> {u.source_label || t({ en: "Official source", ar: "المصدر الرسمي" })}
                        </a>
                      )}
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>

          <Reveal>
            <div className="relative overflow-hidden mt-14 rounded-[1.75rem] bg-navy-deep p-8 md:p-10 text-center">
              <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${IMAGES.brandTexture})`, backgroundSize: "cover" }} />
              <h3 className="relative font-display text-2xl md:text-3xl text-white">{t({ en: "Need help applying these changes to your business?", ar: "تحتاج مساعدة في تطبيق هذه التغييرات على أعمالك؟" })}</h3>
              <Link to="/#contact" data-testid="updates-cta-contact"
                className="relative mt-6 inline-flex items-center gap-2 h-13 px-7 py-3.5 rounded-full bg-gold text-navy-deep font-semibold hover:bg-white transition-colors">
                {t({ en: "Talk to TANSEEQ", ar: "تحدّث مع تنسيق" })} <Arrow className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
